import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, FileText, Building2, LogOut, Users, Bell, Settings as SettingsIcon, PackageCheck, Plus, Plug, Check, BarChart3, Search, ChevronsUpDown, Info, UtensilsCrossed } from "lucide-react";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { BusinessProvider, useBusiness } from "./contexts/BusinessContext";
import { NotificationProvider, useNotifications } from "./contexts/NotificationContext";
import { adminStatus } from "./services/adminService";
import { clearAdminImpersonation, getAdminImpersonation, adminImpersonationHeaders } from "./lib/adminImpersonation";
import { configureApiHeaders } from "./lib/apiHeaders";
import { supabase } from "./lib/supabaseClient";

const ACTIVE_BUSINESS_KEY = "iscan.activeBusinessId";

// Wire up the web implementation of the API header provider once at startup.
// Mobile will provide its own implementation using AsyncStorage / SecureStore.
configureApiHeaders(async ({ businessId, authOnly }) => {
  const selectedBusinessId = authOnly ? undefined : (businessId || localStorage.getItem(ACTIVE_BUSINESS_KEY) || undefined);
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Sign in again.");
  const headers: Record<string, string> = { Authorization: `Bearer ${session.access_token}` };
  if (selectedBusinessId) headers["X-Business-Id"] = selectedBusinessId;
  return { ...headers, ...(adminImpersonationHeaders() as Record<string, string>) };
});

import NotificationPanel from "./components/NotificationPanel";

import DashboardPage from "./pages/app/DashboardPage";
import InvoiceListPage from "./pages/app/InvoiceListPage";
import SuppliersPage from "./pages/app/SuppliersPage";
import ReportsPage from "./pages/app/ReportsPage";
import ParLevelPage from "./pages/app/ParLevelPage";
import MenuCostingPage from "./pages/app/MenuCostingPage";

import LoginPage from "./pages/auth/LoginPage";
import SignUpPage from "./pages/auth/SignUpPage";
import AuthConfirmPage from "./pages/auth/AuthConfirmPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import PostAuthGate from "./pages/auth/PostAuthGate";

import OnboardingChoicePage from "./pages/onboarding/OnboardingChoicePage";
import PendingRequestPage from "./pages/onboarding/PendingRequestPage";
import CreateBusinessPage from "./pages/onboarding/CreateBusinessPage";
import JoinBusinessPage from "./pages/onboarding/JoinBusinessPage";
import OwnerSummaryReportPage from "./pages/onboarding/OwnerSummaryReportPage";
import SettingsPage from "./pages/onboarding/SettingsPage";
import HomePage from "./pages/marketing/HomePage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import PricingPage from "./pages/marketing/PricingPage";
import AboutPage from "./pages/marketing/AboutPage";
import IntegrationsPage from "./pages/onboarding/IntegrationsPage";
import TeamPage from "./pages/app/TeamPage";
import BusinessSettingsPage from "./pages/app/BusinessSettingsPage";
import AdminVerifyPage from "./pages/admin/AdminVerifyPage";
import PlatformAdminPage, {
  AdminAudit,
  AdminBusinesses,
  AdminDashboard,
  AdminIntegrations,
  AdminInvoices,
  AdminIssues,
  AdminRegistrations,
  AdminSuppliers,
  AdminUsers,
} from "./pages/admin/PlatformAdminPage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false } },
});

const FOREST = "#607840";
const GREEN = "#607840";
const PAGE = "#fff";
const SURFACE = "#fff";
const RAISED = "#fff";
const SIDEBAR = "#fefcf8";
const TEXT = "#2a2420";
const MUTED = "#8a8074";
const FAINT = "#aaa093";
const LINE = "#e9e1d4";
const LINE_2 = "#dccfbd";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const LOGO_FONT = "var(--font-brand)";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", permission: "dashboard" },
  { to: "/invoices", icon: FileText, label: "Invoices", permission: "invoices" },
  { to: "/suppliers", icon: Building2, label: "Suppliers", permission: "suppliers" },
  { to: "/par-level", icon: PackageCheck, label: "Par Level", permission: "par_level" },
  { to: "/menu-costing", icon: UtensilsCrossed, label: "Menu Costing", permission: "dashboard" },
  { to: "/reports", icon: FileText, label: "Reports & Export", permission: "reports" },
];

const reportNavItems = [
  { tab: "overview", label: "Overview" },
  { tab: "costing", label: "Costing" },
  { tab: "export", label: "Export" },
];

const supplierNavItems = [
  { view: "suppliers", label: "Suppliers Detail" },
  { view: "inbox", label: "Inbox" },
  { view: "catalog", label: "Item Catalog" },
];

const parLevelNavItems = [
  { view: "check", label: "Daily Check" },
  { view: "history", label: "History" },
  { view: "template", label: "Template" },
];

function ZeroBusinessState() {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-10">
      <div
        className="w-full max-w-4xl rounded-[32px] px-8 py-12 text-center"
        style={{
          backgroundColor: SURFACE,
          border: "1px solid rgba(17,24,39,0.06)",
          boxShadow: "0 20px 48px rgba(15,23,42,0.06)",
        }}
      >
        <h1 className="text-3xl font-semibold tracking-[-0.03em]" style={{ color: TEXT }}>
          Start by joining or creating a business
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-6" style={{ color: MUTED }}>
          Your console is ready. Once you connect to a business, the same layout will show your dashboard,
          invoices, team tools, reports, and settings.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
          <button
            type="button"
            onClick={() => navigate("/join-business")}
            className="rounded-[28px] px-8 py-10 text-left transition-all duration-200"
            style={{
              backgroundColor: SURFACE,
              border: "1px solid rgba(17,24,39,0.08)",
              boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
            }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ background: "linear-gradient(145deg, #F2FBF6 0%, #DFF5E8 100%)", color: FOREST }}
            >
              <Users size={22} />
            </div>
            <p className="mt-8 text-2xl font-semibold" style={{ color: TEXT }}>Join a Business</p>
            <p className="mt-2 text-sm leading-6" style={{ color: MUTED }}>
              Use an invitation code or request access to an existing team.
            </p>
          </button>

          <button
            type="button"
            onClick={() => navigate("/create-business")}
            className="rounded-[28px] px-8 py-10 text-left transition-all duration-200"
            style={{
              background: "linear-gradient(145deg, #F2FBF6 0%, #DFF5E8 100%)",
              border: "1px solid rgba(96,120,64,0.10)",
              boxShadow: "0 18px 40px rgba(96,120,64,0.10)",
            }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl"
              style={{ backgroundColor: "rgba(255,255,255,0.72)", color: FOREST }}
            >
              <Plus size={22} />
            </div>
            <p className="mt-8 text-2xl font-semibold" style={{ color: TEXT }}>Create a Business</p>
            <p className="mt-2 text-sm leading-6" style={{ color: MUTED }}>
              Set up a new workspace and invite teammates when you are ready.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsHubPage() {
  const { memberships, activeBusiness, hasPermission } = useBusiness();
  const navigate = useNavigate();
  const hasBusiness = memberships.length > 0 && Boolean(activeBusiness);
  const [activeId, setActiveId] = useState("account");

  const sidebarGroups = [
    {
      group: "CONFIGURATION",
      items: [
        {
          id: "account",
          label: "Account Preferences",
          icon: SettingsIcon,
          enabled: true,
          description: "Update your profile, notification preferences, and personal account settings.",
          route: "/settings",
          actionLabel: "Open account settings",
        },
        {
          id: "business",
          label: "Business Settings",
          icon: Building2,
          enabled: hasBusiness && hasPermission("business_settings"),
          description: "Update business name, industry, contact details, and workspace-level settings.",
          route: "/business-settings",
          actionLabel: "Open business settings",
        },
      ],
    },
    {
      group: "TEAM",
      items: [
        {
          id: "team",
          label: "Team & Members",
          icon: Users,
          enabled: hasBusiness && hasPermission("team"),
          description: "Manage invite codes, pending member requests, and role-based access for the current business.",
          route: "/team",
          actionLabel: "Open team settings",
        },
      ],
    },
    {
      group: "INTEGRATIONS",
      items: [
        {
          id: "integrations",
          label: "Business Integrations",
          icon: Plug,
          enabled: hasBusiness && hasPermission("integrations"),
          description: "Configure storage exports and connected third-party services for this business.",
          route: "/integrations",
          actionLabel: "Open integrations",
        },
      ],
    },
  ];

  const allItems = sidebarGroups.flatMap((g) => g.items);
  const active = allItems.find((i) => i.id === activeId) ?? allItems[0];

  function renderContent() {
    if (!active.enabled) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-8">
          <p className="text-sm font-medium" style={{ color: TEXT }}>Section unavailable</p>
          <p className="text-sm" style={{ color: MUTED }}>
            Create or join a business first to unlock this section.
          </p>
        </div>
      );
    }
    if (activeId === "account") return <SettingsPage embedded />;
    if (activeId === "business") return <BusinessSettingsPage embedded />;
    if (activeId === "team") return <TeamPage embedded />;
    if (activeId === "integrations") return <IntegrationsPage embedded />;
    return null;
  }

  return (
    <div className="flex h-full" style={{ backgroundColor: PAGE }}>
      {/* Settings sidebar */}
      <aside
        className="w-56 flex-shrink-0 flex flex-col py-6"
        style={{ backgroundColor: SIDEBAR, borderRight: `1px solid ${LINE}` }}
      >
        <p className="px-4 mb-5 text-sm font-semibold" style={{ color: TEXT }}>Settings</p>
        {sidebarGroups.map(({ group, items }) => (
          <div key={group} className="mb-5">
            <p
              className="px-4 mb-1 text-[10px] font-semibold tracking-widest uppercase"
              style={{ color: MUTED }}
            >
              {group}
            </p>
            {items.map(({ id, label, icon: Icon, enabled }) => {
              const selected = id === activeId;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setActiveId(id); }}
                  disabled={!enabled}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: selected ? ACCENT_WEAK : "transparent",
                    color: selected ? FOREST : TEXT,
                    fontWeight: selected ? 600 : 400,
                    borderRight: selected ? `2px solid ${FOREST}` : "2px solid transparent",
                  }}
                >
                  <Icon size={14} style={{ flexShrink: 0 }} />
                  {label}
                </button>
              );
            })}
          </div>
        ))}
      </aside>

      {/* Content area — fills remaining width, scrolls independently */}
      <main className="flex-1 overflow-auto" style={{ backgroundColor: PAGE }}>
        {renderContent()}
      </main>
    </div>
  );
}

// ── Sidebar layout ────────────────────────────────────────────────

function AppLayout() {
  const { user, signOut, sessionWarning, secondsUntilLogout, resetActivity } = useAuth();
  const { activeBusiness, memberships, setActiveBusiness, clearActiveBusiness, hasPermission, impersonation, isReadOnlyImpersonation } = useBusiness();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
  const [businessSearch, setBusinessSearch] = useState("");
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  // Touch devices (iPad/iPhone) have no hover, so we drive the sidebar by tap instead.
  const [canHover, setCanHover] = useState(true);
  const businessMenuRef = useRef<HTMLDivElement>(null);
  const userMenuTriggerRef = useRef<HTMLDivElement>(null);
  const userMenuPanelRef = useRef<HTMLDivElement>(null);

  async function handleSignOut() {
    clearActiveBusiness();
    await signOut();
  }

  function switchBusiness(business: typeof memberships[number]) {
    setActiveBusiness(business);
    queryClient.clear();
    setBusinessMenuOpen(false);
    navigate("/");
  }
  function exitImpersonation() {
    clearAdminImpersonation();
    clearActiveBusiness();
    queryClient.clear();
    window.location.assign("/admin/users");
  }
  const canViewOwnerSummary = memberships.length > 1 && hasPermission("owner_summary");
  const activeBusinessRole = isReadOnlyImpersonation
    ? "Read-only support view"
    : activeBusiness?.role === "owner" ? "Owner workspace" : "Member workspace";
  const visibleNavItems = navItems.filter((item) => hasPermission(item.permission));
  const filteredBusinesses = memberships.filter((business) =>
    business.name.toLowerCase().includes(businessSearch.trim().toLowerCase()),
  );
  const userInitial = user?.email?.[0]?.toUpperCase() ?? "U";
  const reportsActive = location.pathname === "/reports";
  const activeReportTab = reportsActive ? new URLSearchParams(location.search).get("tab") || "overview" : "";
  const suppliersActive = location.pathname === "/suppliers";
  const activeSupplierView = suppliersActive ? new URLSearchParams(location.search).get("view") || "suppliers" : "";
  const parLevelActive = location.pathname === "/par-level";
  const activeParLevelView = parLevelActive ? new URLSearchParams(location.search).get("view") || "check" : "";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (businessMenuRef.current && !businessMenuRef.current.contains(target)) {
        setBusinessMenuOpen(false);
      }
      if (
        userMenuOpen &&
        !userMenuTriggerRef.current?.contains(target) &&
        !userMenuPanelRef.current?.contains(target)
      ) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    setCanHover(mq.matches);
    const onChange = (event: MediaQueryListEvent) => {
      setCanHover(event.matches);
      if (!event.matches) setSidebarExpanded(false);
    };
    mq.addEventListener("change", onChange);
    // iPads often report `hover: hover` even without a mouse, which leaves the
    // hover handlers bound and breaks tap-to-close on iOS. Treat the first real
    // touch as proof this is a touch device and drop hover-driven expansion.
    const onFirstTouch = () => setCanHover(false);
    window.addEventListener("touchstart", onFirstTouch, { once: true, passive: true });
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("touchstart", onFirstTouch);
    };
  }, []);

  return (
    <div
      className="grid h-screen overflow-hidden"
      style={{
        gridTemplateColumns: "64px 1fr",
        backgroundColor: PAGE,
      }}
    >
      <aside
        className="relative z-30 min-h-0 w-16 flex-shrink-0"
        onMouseEnter={canHover ? () => setSidebarExpanded(true) : undefined}
        onMouseLeave={canHover ? () => setSidebarExpanded(false) : undefined}
        onFocus={canHover ? () => setSidebarExpanded(true) : undefined}
        onBlur={canHover ? (event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setSidebarExpanded(false);
          }
        } : undefined}
      >
        <div
          className="absolute inset-y-0 left-0 flex min-h-0 flex-col overflow-hidden"
          style={{
            width: sidebarExpanded ? 236 : 64,
            backgroundColor: SIDEBAR,
            borderRight: `1px solid ${LINE}`,
            boxShadow: sidebarExpanded ? "10px 0 30px rgba(80,55,30,0.08)" : "none",
            transition: "width 240ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 240ms ease",
          }}
        >
          <div className="relative h-[60px] flex-shrink-0">
            <button
              type="button"
              onClick={() => setSidebarExpanded((open) => !open)}
              aria-label={sidebarExpanded ? "Collapse menu" : "Expand menu"}
              className="absolute inset-y-0 left-0 grid w-16 place-items-center"
              style={{ cursor: "pointer" }}
            >
              <img
                src="/login-logo.png"
                alt="BillLens"
                className="h-9 w-9 rounded-[6px] object-cover"
              />
            </button>
            <div
              className="absolute left-[62px] top-1/2 whitespace-nowrap"
              style={{
                color: TEXT,
                opacity: sidebarExpanded ? 1 : 0,
                transform: `translate(${sidebarExpanded ? "0" : "6px"}, -50%)`,
                transition: "opacity 160ms ease 60ms, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                fontFamily: LOGO_FONT,
                fontSize: 20,
                fontWeight: 400,
                lineHeight: 1,
                letterSpacing: 0,
              }}
            >
              Bill<span style={{ color: GREEN }}>Lens</span>
            </div>
            <span
              className="absolute right-5 top-1/2 whitespace-nowrap rounded-full px-2 py-[2px] text-[9.5px]"
              style={{
                color: FAINT,
                border: `1px solid ${LINE}`,
                opacity: sidebarExpanded ? 1 : 0,
                transform: "translateY(-50%)",
                transition: "opacity 160ms ease 80ms",
              }}
            >
              v2.0
            </span>
          </div>

          <nav
            className="flex min-h-0 flex-1 flex-col items-start gap-2 overflow-y-auto px-3 py-4"
          >
            {visibleNavItems.map(({ to, icon: Icon, label }) => {
              const isReportsItem = to === "/reports";
              const isSuppliersItem = to === "/suppliers";
              const isParLevelItem = to === "/par-level";
              const showReportSubnav = isReportsItem && reportsActive && sidebarExpanded;
              const showSupplierSubnav = isSuppliersItem && suppliersActive && sidebarExpanded;
              const showParLevelSubnav = isParLevelItem && parLevelActive && sidebarExpanded;
              return (
                <div key={to} className="w-full">
                  <NavLink
                    to={to}
                    end={to === "/"}
                    title={label}
                    onClick={() => {
                      // On tap, reveal the sub-tabs for items that have them
                      // (hover can't do this on touch devices).
                      if (isReportsItem || isSuppliersItem || isParLevelItem) {
                        setSidebarExpanded(true);
                      }
                    }}
                    className="relative block h-10 overflow-hidden rounded-[9px] text-[13.5px]"
                    style={({ isActive }) => ({
                      width: sidebarExpanded ? 212 : 40,
                      color: isActive ? GREEN : MUTED,
                      fontWeight: isActive ? 700 : 500,
                      backgroundColor: "transparent",
                      border: "1px solid transparent",
                      transition: "width 240ms cubic-bezier(0.22, 1, 0.36, 1), color 160ms ease, font-weight 160ms ease",
                    })}
                  >
                    <span className="absolute inset-y-0 left-0 grid w-10 place-items-center">
                      <Icon size={17} strokeWidth={1.7} />
                    </span>
                    <span
                      className="absolute left-12 top-1/2 max-w-[118px] -translate-y-1/2 truncate whitespace-nowrap"
                      style={{
                        opacity: sidebarExpanded ? 1 : 0,
                        transform: `translate(${sidebarExpanded ? "0" : "4px"}, -50%)`,
                        transition: "opacity 150ms ease 70ms, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                      }}
                    >
                      {label}
                    </span>
                  </NavLink>

                  {isReportsItem && (
                    <div
                      className="overflow-hidden"
                      aria-hidden={!showReportSubnav}
                      style={{
                        width: sidebarExpanded ? 212 : 40,
                        maxHeight: showReportSubnav ? 196 : 0,
                        opacity: showReportSubnav ? 1 : 0,
                        transform: `translateY(${showReportSubnav ? "0" : "-8px"})`,
                        transition: "max-height 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 170ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                        pointerEvents: showReportSubnav ? "auto" : "none",
                      }}
                    >
                      <div className="ml-8 mt-1 space-y-1 pl-2">
                        {reportNavItems.map((item) => {
                          const active = activeReportTab === item.tab;
                          const toPath = item.tab === "overview" ? "/reports" : `/reports?tab=${item.tab}`;
                          return (
                            <button
                              key={item.tab}
                              type="button"
                              onClick={() => navigate(toPath)}
                              className="block w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors"
                              style={{
                                color: active ? GREEN : MUTED,
                                fontWeight: active ? 700 : 500,
                                backgroundColor: "transparent",
                              }}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {isSuppliersItem && (
                    <div
                      className="overflow-hidden"
                      aria-hidden={!showSupplierSubnav}
                      style={{
                        width: sidebarExpanded ? 212 : 40,
                        maxHeight: showSupplierSubnav ? 120 : 0,
                        opacity: showSupplierSubnav ? 1 : 0,
                        transform: `translateY(${showSupplierSubnav ? "0" : "-8px"})`,
                        transition: "max-height 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 170ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                        pointerEvents: showSupplierSubnav ? "auto" : "none",
                      }}
                    >
                      <div className="ml-8 mt-1 space-y-1 pl-2">
                        {supplierNavItems.map((item) => {
                          const active = activeSupplierView === item.view;
                          const toPath = item.view === "suppliers" ? "/suppliers" : `/suppliers?view=${item.view}`;
                          return (
                            <button
                              key={item.view}
                              type="button"
                              onClick={() => navigate(toPath)}
                              className="block w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors"
                              style={{
                                color: active ? GREEN : MUTED,
                                fontWeight: active ? 700 : 500,
                                backgroundColor: "transparent",
                              }}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {isParLevelItem && (
                    <div
                      className="overflow-hidden"
                      aria-hidden={!showParLevelSubnav}
                      style={{
                        width: sidebarExpanded ? 212 : 40,
                        maxHeight: showParLevelSubnav ? 120 : 0,
                        opacity: showParLevelSubnav ? 1 : 0,
                        transform: `translateY(${showParLevelSubnav ? "0" : "-8px"})`,
                        transition: "max-height 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 170ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                        pointerEvents: showParLevelSubnav ? "auto" : "none",
                      }}
                    >
                      <div className="ml-8 mt-1 space-y-1 pl-2">
                        {parLevelNavItems.map((item) => {
                          const active = activeParLevelView === item.view;
                          const toPath = item.view === "check" ? "/par-level" : `/par-level?view=${item.view}`;
                          return (
                            <button
                              key={item.view}
                              type="button"
                              onClick={() => navigate(toPath)}
                              className="block w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors"
                              style={{
                                color: active ? GREEN : MUTED,
                                fontWeight: active ? 700 : 500,
                                backgroundColor: "transparent",
                              }}
                            >
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div
            ref={userMenuTriggerRef}
            className="relative mt-auto h-[59px]"
            style={{ borderTop: `1px solid ${LINE}` }}
          >
            <button
              type="button"
              onClick={() => setUserMenuOpen((open) => !open)}
              className="relative h-full w-full rounded-[9px] text-left"
            >
              <span
                className="absolute top-[13px] grid h-8 w-8 place-items-center rounded-[10px] text-sm font-semibold"
                style={{
                  left: sidebarExpanded ? 13 : 16,
                  background: "linear-gradient(160deg,#607840,#2a2420)",
                  color: SURFACE,
                  transition: "left 240ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                {userInitial}
              </span>
              <span
                className="absolute left-14 top-1/2 min-w-0 -translate-y-1/2"
                style={{
                  opacity: sidebarExpanded ? 1 : 0,
                  transform: `translate(${sidebarExpanded ? "0" : "4px"}, -50%)`,
                  transition: "opacity 150ms ease 70ms, transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                <span className="block max-w-[150px] truncate text-[12.5px] font-semibold" style={{ color: TEXT }}>
                  {user?.email ?? "Account"}
                </span>
                <span className="block whitespace-nowrap text-[11.5px]" style={{ color: MUTED }}>
                  {activeBusiness ? activeBusinessRole : "No workspace"}
                </span>
              </span>
            </button>

          </div>
        </div>

        {userMenuOpen && (
          <div
            ref={userMenuPanelRef}
            className="absolute bottom-[62px] left-[13px] z-50 w-[210px] overflow-hidden rounded-[14px]"
            style={{ backgroundColor: SURFACE, border: `1px solid ${LINE_2}`, boxShadow: "0 18px 44px rgba(80,55,30,.16)" }}
          >
            <div className="px-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
              <p className="truncate text-xs font-medium" style={{ color: MUTED }}>{user?.email}</p>
            </div>
            <div className="p-2">
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/console-settings");
                }}
                className="flex w-full items-center gap-3 rounded-[9px] px-3 py-2.5 text-left text-sm transition-colors"
                style={{ color: TEXT }}
              >
                <SettingsIcon size={16} />
                Settings
              </button>
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/about");
                }}
                className="mt-1 flex w-full items-center gap-3 rounded-[9px] px-3 py-2.5 text-left text-sm transition-colors"
                style={{ color: TEXT }}
              >
                <Info size={16} />
                About Bill Lens
              </button>
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  handleSignOut();
                }}
                className="mt-1 flex w-full items-center gap-3 rounded-[9px] px-3 py-2.5 text-left text-sm"
                style={{ color: "#B91C1C", backgroundColor: "rgba(185,28,28,0.08)" }}
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </div>
        )}
      </aside>

      <main
        className="flex min-w-0 flex-col overflow-hidden"
        onClick={() => { if (sidebarExpanded) setSidebarExpanded(false); }}
      >
        <div
          className="sticky top-0 z-20 flex h-[60px] flex-shrink-0 items-center gap-3 px-6"
          style={{
            backgroundColor: "color-mix(in srgb, #fff 86%, transparent)",
            backdropFilter: "blur(8px)",
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <div className="relative min-w-0" ref={businessMenuRef}>
            <button
              type="button"
              onClick={() => {
                setBusinessMenuOpen((open) => !open);
                if (businessMenuOpen) setBusinessSearch("");
              }}
              className="flex h-[38px] max-w-[360px] items-center gap-2.5 rounded-full px-3.5 text-left text-[13px] font-semibold"
              style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, boxShadow: SHADOW, color: TEXT }}
            >
              <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: GREEN }} />
              <span className="truncate">{activeBusiness?.name ?? "Choose a business"}</span>
              <ChevronsUpDown size={15} style={{ color: MUTED, flexShrink: 0 }} />
            </button>

            {businessMenuOpen && memberships.length > 0 && (
              <div
                className="absolute left-0 top-[calc(100%+10px)] z-30 w-[min(420px,calc(100vw-3rem))] overflow-hidden rounded-[14px]"
                style={{ backgroundColor: SURFACE, border: `1px solid ${LINE_2}`, boxShadow: "0 20px 46px rgba(80,55,30,.16)" }}
              >
                <div className="px-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
                  <label
                    className="flex items-center gap-3 rounded-[9px] px-3 py-2.5"
                    style={{ backgroundColor: RAISED, border: `1px solid ${LINE}` }}
                  >
                    <Search size={17} style={{ color: MUTED }} />
                    <input
                      value={businessSearch}
                      onChange={(event) => setBusinessSearch(event.target.value)}
                      placeholder="Find workspace..."
                      className="w-full bg-transparent text-sm outline-none"
                      style={{ color: TEXT }}
                    />
                  </label>
                </div>

                <div className="py-2">
                  {filteredBusinesses.map((business) => {
                    const selected = activeBusiness?.id === business.id;
                    return (
                      <button
                        key={business.id}
                        type="button"
                        onClick={() => switchBusiness(business)}
                        className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors"
                        style={{ backgroundColor: selected ? ACCENT_WEAK : "transparent", color: TEXT }}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-semibold" style={{ color: TEXT }}>
                            {business.name}
                          </p>
                          <p className="mt-0.5 text-[11px]" style={{ color: MUTED }}>
                            {business.role === "owner" ? "Owner workspace" : "Member workspace"}
                          </p>
                        </div>
                        {selected && <Check size={17} style={{ color: GREEN }} />}
                      </button>
                    );
                  })}
                  {filteredBusinesses.length === 0 && (
                    <div className="px-5 py-6 text-sm" style={{ color: MUTED }}>
                      No workspaces found.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2" style={{ borderTop: `1px solid ${LINE}` }}>
                  <button
                    type="button"
                    onClick={() => { setBusinessMenuOpen(false); setBusinessSearch(""); navigate("/create-business"); }}
                    className="flex w-full items-center justify-center gap-2 px-4 py-3 text-left transition-colors"
                    style={{ color: GREEN }}
                  >
                    <Plus size={14} style={{ color: GREEN }} />
                    <span className="text-[13px] font-medium">Add business</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBusinessMenuOpen(false); setBusinessSearch(""); navigate("/join-business"); }}
                    className="flex w-full items-center justify-center gap-2 px-4 py-3 text-left transition-colors"
                    style={{ color: GREEN, borderLeft: `1px solid ${LINE}` }}
                  >
                    <Users size={14} style={{ color: GREEN }} />
                    <span className="text-[13px] font-medium">Join business</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {canViewOwnerSummary && (
              <button
                type="button"
                onClick={() => navigate("/owner-summary")}
                className="grid h-[38px] w-[38px] place-items-center rounded-full"
                style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, color: MUTED }}
                title="Cross-business report"
              >
                <BarChart3 size={17} strokeWidth={1.7} />
              </button>
            )}

            {!isReadOnlyImpersonation && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setNotifOpen((v) => !v)}
                  className="relative grid h-[38px] w-[38px] place-items-center rounded-full"
                  style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, color: MUTED }}
                  title="Notifications"
                >
                  <Bell size={17} strokeWidth={1.7} />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold leading-none text-white tabular-nums"
                      style={{ backgroundColor: GREEN, border: `2px solid ${PAGE}` }}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate("/invoices")}
              className="grid h-[38px] w-[38px] place-items-center rounded-full"
              style={{ backgroundColor: SURFACE, border: `1px solid ${LINE}`, color: MUTED }}
              title="Add invoice"
            >
              <Plus size={17} strokeWidth={1.7} />
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {memberships.length === 0 ? <ZeroBusinessState /> : <Outlet />}
        </div>
      </main>
      {isReadOnlyImpersonation && (
        <div
          className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-full px-4 py-2 text-sm shadow-xl"
          style={{ backgroundColor: "#17212b", color: "white", border: "1px solid rgba(255,255,255,0.12)" }}
        >
          <span className="font-semibold">
            Viewing as {impersonation?.targetUser.name || impersonation?.targetUser.email || impersonation?.targetUser.id}
          </span>
          <span style={{ color: "rgba(255,255,255,0.58)" }}>Read-only</span>
          <button
            type="button"
            onClick={exitImpersonation}
            className="rounded-full px-3 py-1 text-xs font-bold"
            style={{ backgroundColor: "white", color: "#17212b" }}
          >
            Exit
          </button>
        </div>
      )}

      {sessionWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}>
          <div
            className="mx-4 w-full max-w-sm rounded-[24px] p-8 text-center"
            style={{ backgroundColor: SURFACE, boxShadow: "0 24px 64px rgba(15,23,42,0.18)", border: "1px solid rgba(17,24,39,0.08)" }}
          >
            <div
              className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-[18px]"
              style={{ background: "linear-gradient(145deg, #FEF3C7, #FDE68A)", color: "#92400E" }}
            >
              <LogOut size={24} />
            </div>
            <h2 className="text-lg font-semibold" style={{ color: TEXT }}>Still there?</h2>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: MUTED }}>
              You'll be signed out in{" "}
              <span className="font-semibold tabular-nums" style={{ color: TEXT }}>
                {Math.floor(secondsUntilLogout / 60)}:{String(secondsUntilLogout % 60).padStart(2, "0")}
              </span>{" "}
              due to inactivity.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={resetActivity}
                className="w-full rounded-full py-2.5 text-sm font-semibold text-white"
                style={{ backgroundColor: GREEN }}
              >
                Stay logged in
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="w-full rounded-full py-2.5 text-sm font-medium"
                style={{ color: MUTED }}
              >
                Sign out now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Protected route (no layout) ───────────────────────────────────

function ProtectedRoute() {
  const { session, loading } = useAuth();
  const location = useLocation();
  const adminStatusQuery = useQuery({
    queryKey: ["platform-admin-route-status"],
    queryFn: adminStatus,
    enabled: Boolean(session),
    retry: false,
  });

  if (loading || (session && adminStatusQuery.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fff" }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#607840", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  if (!location.pathname.startsWith("/admin") && adminStatusQuery.data?.isPlatformAdmin) {
    return <Navigate to={adminStatusQuery.data.verified ? "/admin" : "/admin/verify"} replace />;
  }

  return <Outlet />;
}

function PermissionRoute({ permission }: { permission: string }) {
  const { hasPermission } = useBusiness();
  if (!hasPermission(permission)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// ── Protected route (with sidebar layout) ────────────────────────

function ProtectedAppRoute() {
  const { session, loading } = useAuth();
  const { activeBusiness, memberships, loading: businessLoading } = useBusiness();
  const adminStatusQuery = useQuery({
    queryKey: ["platform-admin-route-status"],
    queryFn: adminStatus,
    enabled: Boolean(session),
    retry: false,
  });

  if (loading || businessLoading || (session && adminStatusQuery.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#fff" }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#607840", borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;

  if (adminStatusQuery.data?.isPlatformAdmin && !getAdminImpersonation()) {
    return <Navigate to={adminStatusQuery.data.verified ? "/admin" : "/admin/verify"} replace />;
  }

  if (memberships.length > 0 && !activeBusiness) return <Navigate to="/" replace />;
  return <AppLayout />;
}

// ── Root ──────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BusinessProvider>
          <NotificationProvider>
            <BrowserRouter>
              <Routes>
                {/* Public pages */}
                <Route path="/home"     element={<HomePage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/pricing"  element={<PricingPage />} />
                <Route path="/about"  element={<AboutPage />} />
                <Route path="/login"  element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/confirm" element={<AuthConfirmPage />} />

                {/* Protected — no sidebar (onboarding + gate) */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/admin/verify"     element={<AdminVerifyPage />} />
                  <Route path="/gate"            element={<PostAuthGate />} />
                  <Route path="/businesses"      element={<Navigate to="/" replace />} />
                  <Route path="/onboarding"      element={<OnboardingChoicePage />} />
                  <Route path="/pending"         element={<PendingRequestPage />} />
                  <Route path="/create-business" element={<CreateBusinessPage />} />
                  <Route path="/join-business"   element={<JoinBusinessPage />} />
                  <Route element={<PermissionRoute permission="owner_summary" />}>
                    <Route path="/owner-summary" element={<OwnerSummaryReportPage />} />
                  </Route>
                </Route>

                {/* Platform admin — separate from customer business roles */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/admin" element={<PlatformAdminPage />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="registrations" element={<AdminRegistrations />} />
                  <Route path="businesses" element={<AdminBusinesses />} />
                    <Route path="suppliers" element={<AdminSuppliers />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="invoices" element={<AdminInvoices />} />
                    <Route path="integrations" element={<AdminIntegrations />} />
                    <Route path="issues" element={<AdminIssues />} />
                    <Route path="audit" element={<AdminAudit />} />
                  </Route>
                </Route>

                {/* Protected — with sidebar (main app) */}
                <Route element={<ProtectedAppRoute />}>
                  <Route element={<PermissionRoute permission="dashboard" />}>
                    <Route path="/" element={<DashboardPage />} />
                  </Route>
                  <Route element={<PermissionRoute permission="invoices" />}>
                    <Route path="/invoices" element={<InvoiceListPage />} />
                  </Route>
                  <Route element={<PermissionRoute permission="suppliers" />}>
                    <Route path="/suppliers" element={<SuppliersPage />} />
                  </Route>
                  <Route element={<PermissionRoute permission="par_level" />}>
                    <Route path="/par-level" element={<ParLevelPage />} />
                  </Route>
                  <Route path="/menu-costing" element={<MenuCostingPage />} />
                  <Route element={<PermissionRoute permission="reports" />}>
                    <Route path="/reports" element={<ReportsPage />} />
                  </Route>
                  <Route element={<PermissionRoute permission="team" />}>
                    <Route path="/team" element={<TeamPage />} />
                  </Route>
                  <Route path="/console-settings" element={<SettingsHubPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route element={<PermissionRoute permission="integrations" />}>
                    <Route path="/integrations" element={<IntegrationsPage />} />
                  </Route>
                  <Route element={<PermissionRoute permission="business_settings" />}>
                    <Route path="/business-settings" element={<BusinessSettingsPage />} />
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </NotificationProvider>
        </BusinessProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
